import { AttachmentFileIcon } from '@/components/icons/AttachmentFileIcon';
import { DocumentPreview } from '@/components/DocumentPreview';
import { changeResourceConfidentiality } from '@/services/permissions';
import { alert } from '@/services/alert';
import { isResourceRestricted } from '@bariendo/lib/permissions';
import { ConfidentialityLevel } from '@bariendo/lib/constants';
import { ActionIcon, Box, Group, Modal, Paper, Stack, Text, Tooltip } from '@mantine/core';
import { Switch } from '@mantine/core';
import { createReference } from '@medplum/core';
import { DocumentReference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  IconCalendarStats,
  IconDownload,
  IconEye,
  IconLock,
  IconTags,
  IconUserCircle,
  IconX,
} from '@tabler/icons-react';
import { RestrictedContentManager } from '@/pages/PatientTimelinePage/RestrictedContentManager';
import { ReactElement, useEffect, useState } from 'react';
import { formatDate, formatToAdobeSpectrumFormat } from '@/services/date';
import { formatFileSize } from '@/services/attachments';

type DocumentCardProps = {
  doc: DocumentReference;
  onDownload: (doc: DocumentReference) => void;
  patientId?: string;
  organizationId?: string;
  canUserAccessRestrictedContent: boolean;
  canManageRestrictedContent: boolean;
};

export function DocumentCard({
  doc,
  onDownload,
  patientId,
  organizationId,
  canUserAccessRestrictedContent,
  canManageRestrictedContent,
}: DocumentCardProps): ReactElement {
  const medplum = useMedplum();
  const attachment = doc.content?.[0]?.attachment;
  const author = doc.author?.[0]?.display;
  const title = attachment?.title || `untitled-${doc.date ? formatDate(doc.date, 'YYYY-MM-DD') : 'unknown'}`;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleToPatient, setVisibleToPatient] = useState(!isResourceRestricted(doc));
  const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false);

  useEffect(() => {
    setVisibleToPatient(!isResourceRestricted(doc));
  }, [doc]);

  const handleView = () => {
    setIsModalOpen(true);
  };

  const handleVisibilityChange = async (nextVisible: boolean) => {
    if (!canManageRestrictedContent) {
      alert.error("You don't have permission to change patient visibility for documents.");
      return;
    }

    if (isUpdatingVisibility) return;

    setIsUpdatingVisibility(true);
    setVisibleToPatient(nextVisible);
    try {
      await changeResourceConfidentiality(medplum, {
        resourceRef: createReference(doc),
        confidentialityLevel: nextVisible ? ConfidentialityLevel.NORMAL : ConfidentialityLevel.RESTRICTED,
      });
    } catch {
      // changeResourceConfidentiality already shows an alert; just revert local state
      setVisibleToPatient(!nextVisible);
    } finally {
      setIsUpdatingVisibility(false);
    }
  };

  return (
    <>
      <Paper p="sm" withBorder radius="md" bg="gray.0">
        <RestrictedContentManager isRestricted={isResourceRestricted(doc) && !canUserAccessRestrictedContent}>
          <Box>
            <Group mb={10}>
              <Group justify="flex-start" gap={0} flex={1}>
                <AttachmentFileIcon attachment={attachment} />
                <Tooltip label={title}>
                  <Text flex={1} size="sm" fw={700} lineClamp={1} ml={5}>
                    {title}
                  </Text>
                </Tooltip>
              </Group>
              {attachment?.url && (
                <Tooltip label="Download document">
                  <ActionIcon variant="light" size="sm" onClick={() => onDownload(doc)}>
                    <IconDownload size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
              {attachment?.url && (
                <Tooltip label="Preview document">
                  <ActionIcon variant="light" size="sm" onClick={handleView}>
                    <IconEye size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
            <Box mb={10}>
              <DocumentPreview
                content={doc.content?.[0]}
                height={300}
                width="100%"
                patientId={patientId}
                organizationId={organizationId}
                source="documents pane"
              />
            </Box>
            <Stack gap={4}>
              {attachment?.contentType && (
                <Group gap={6}>
                  <IconTags size={14} color="var(--mantine-color-dimmed)" />
                  <Group gap={2}>
                    <Text component="span" fw={700} size="10px">
                      Type:
                    </Text>
                    <Text size="xs">
                      {attachment.contentType.split('/')[1]?.toUpperCase() || attachment.contentType}
                    </Text>
                    <Text size="xs">{attachment.size && ` - ${formatFileSize(attachment.size).toLowerCase()}`}</Text>
                  </Group>
                </Group>
              )}
              {author && (
                <Group gap={6}>
                  <IconUserCircle size={14} color="var(--mantine-color-dimmed)" />
                  <Group gap={2}>
                    <Text component="span" fw={700} size="10px">
                      Uploaded by:
                    </Text>
                    <Text size="xs">{author}</Text>
                  </Group>
                </Group>
              )}
              <Group gap={6}>
                {doc.date && (
                  <>
                    <IconCalendarStats size={14} color="var(--mantine-color-dimmed)" />
                    <Group gap={2}>
                      <Text component="span" fw={700} size="10px">
                        Date:
                      </Text>
                      <Text size="xs">{formatToAdobeSpectrumFormat(doc.date)}</Text>
                    </Group>
                  </>
                )}

                {isResourceRestricted(doc) && (
                  <Tooltip label="This document contains sensitive information">
                    <IconLock size={16} style={{ marginLeft: 'auto' }} />
                  </Tooltip>
                )}
              </Group>

              {canManageRestrictedContent && (
                <Group justify="space-between" mt={6}>
                  <Text size="xs" fw={600}>
                    Visible to patient
                  </Text>
                  <Switch
                    checked={visibleToPatient}
                    onChange={(event) => handleVisibilityChange(event.currentTarget.checked)}
                    disabled={isUpdatingVisibility}
                    size="sm"
                  />
                </Group>
              )}
            </Stack>
          </Box>
        </RestrictedContentManager>
      </Paper>
      <Modal
        w={'95vw'}
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={doc.content?.[0]?.attachment?.title || 'Document Preview'}
        centered
        size="95vw"
        p="0"
        closeButtonProps={{
          icon: <IconX size={20} />,
        }}
      >
        <DocumentPreview content={doc.content?.[0]} height="35vw" width="100%" />
      </Modal>
    </>
  );
}
